import React, { useEffect, useRef, useState } from "react";
import {
    Article,
    Permission, PermissionAction, useGetArticleQuery,
} from "../../../__generated__/api";
import Resizable from "../../../components/resizable/Resizable";
import TopPanel from "../../../components/top-panel/TopPanel";
import { Paths, useParameterFilters, useParams } from "../../../contexts/Routing";
import useCurrentSession from "../../../contexts/session";
import { ErrorView } from "../../error/Error";
import Styles from "./AnswerDetails.module.scss";
import ArticleView, { createLoadingArticle } from "./Article";
import ArticleTags from "./article-tags/ArticleTags";
import Proposed from "./proposals";
import SearchBar from "./SearchBar";
import RouterLink from "../../../components/theme/button/RouterLink";
import Icon from "../../../components/theme/icon/Icon";

export default function AnswerDetails() {
    const { id: articleId } = useParams(Paths.ANSWERS_GET);
    const { id: idWithVersion, versionId } = useParams(Paths.ANSWERS_GET_VERSION);

    const id = articleId ?? idWithVersion;
    const isNewOrProposed = id === "new" || id === "proposed";
    const { data, error, loading: isLoading } = useGetArticleQuery({
        skip: isNewOrProposed,
        fetchPolicy: versionId ? "no-cache" : undefined,
        variables: {
            id,
            versionId,
        },
    });

    const ref = useRef<HTMLDivElement>(null);

    const [article, setArticle] = useState(data?.article);
    useEffect(() => {
        if (data?.article) {
            setArticle(data?.article);
            ref?.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, [data?.article?.id]);
    const [parameters, setSearch] = useParameterFilters<{search: string}>(Paths.ANSWERS_LIST);
    const { search } = parameters ?? {};

    const [isSearching, setIsSearching] = useState(false);
    const { permissions, isLoggedIn } = useCurrentSession();
    const isReadOnly = !permissions.has(Permission.ArticleModify)
        || (article?.currentUserPermission != null
            && article?.currentUserPermission !== PermissionAction.Modify);

    let body;
    if (id === "proposed") {
        body = <Proposed />;
    } else if (error) {
        body = <ErrorView message={error.message} />;
    } else if (!article && !isNewOrProposed) {
        body = (
            <ArticleView
                key={123}
                isLoading
                isReadOnly
                article={createLoadingArticle()}
            />
        );
    } else {
        body = (
            <ArticleView
                isReadOnly={isReadOnly}
                key={article?.id}
                isNewArticle={isNewOrProposed}
                article={article as Article}
                scrollRef={ref}
            />
        );
    }
    return (
        <div className={Styles.details}>
            {isLoggedIn && (
                <TopPanel
                    title="Answers"
                    ref={ref}
                    buttons={!isReadOnly ? (
                        <RouterLink
                            to={Paths.ANSWERS_GET}
                            params={{ id: "proposed" }}
                            size="small"
                            variant="ghost-gray"
                            prefixIcon={<Icon.ArtificialIntelligence />}
                        />
                    ) : undefined}
                />
            )}

            <div className={Styles.container}>
                <Resizable
                    direction="e"
                    maxSize={500}
                    minSize={200}
                    defaultSize={350}
                    className={Styles["side-panel"]}
                >
                    <SearchBar setIsSearching={setIsSearching} search={search} setSearch={value => setSearch("search", value)} />
                    {!isSearching && (
                        <ArticleTags isReadOnly={isReadOnly} article={article as Article} />
                    )}
                </Resizable>

                {body}

            </div>
        </div>
    );
}
